import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { sprintf } from 'sprintf-js';

import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { BodyMicro } from 'components/Text/BodyMicro';
import { useUser } from 'contexts/UserEnvironmentContext';
import type { SearchAlgoliaInternshipHit } from 'modules/search/algolia/types/SearchAlgoliaInternshipHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitOwnerOrgBanner } from 'modules/search/components/Hit/OrgOwnerBanner/SearchHitOwnerOrgBanner';
import { SearchHitSaveButtonContainer } from 'modules/search/components/Hit/SaveButton/SearchHitSaveButtonContainer';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
  SearchHitSaveButtonHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getWorkLocationString } from 'utils/address/getWorkLocationString';
import { distanceInWordsToNow } from 'utils/date';

import { SearchInternshipHitInfo } from './SearchInternshipHitInfo';

type Props = {
  hit: SearchAlgoliaInternshipHit;
  href: string;
  trackClick?: () => void;
  showLabel?: boolean;
  hideCta?: boolean;
  hideOwnerBanner?: boolean;
  highlighted?: boolean;
  application?: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  };
  actions?: ReactNode;
};

export function SearchInternshipHit({
  hit,
  href,
  trackClick,
  showLabel,
  hideCta,
  hideOwnerBanner,
  highlighted,
  application,
  actions,
}: Props) {
  const { user } = useUser();

  const {
    objectID,
    name,
    published,
    city,
    state,
    country,
    orgID,
    orgName,
    orgType,
    logo,
    locationType,
    remoteZone,
    isPostedAnonymously,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [remoteZone, city, country, state],
  );

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {sprintf(getText('Posted %(postedAt)s'), {
            postedAt: distanceInWordsToNow(fromUnixTime(published)),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [published]);

  const showSaveButton = !hideCta;

  const ownerOrg = user ? user.orgs.find((o) => o.id === orgID) : null;

  return (
    <SearchHitHolder data-qa-id="search-result" data-hit-id={objectID}>
      {!hideOwnerBanner && ownerOrg && (
        <SearchHitOwnerOrgBanner org={ownerOrg} hit={hit} />
      )}

      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          roundedTop={hideOwnerBanner || !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={undefined}
              image={undefined}
              source={undefined}
              name={name}
              logo={logo || undefined}
            />
          </SearchHitImageHolder>

          <SearchInternshipHitInfo
            application={application}
            isPostedAnonymously={isPostedAnonymously}
            listingStats={listingStats}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            showLabel={showLabel}
          />
        </SearchHitInteractiveContentWrapper>

        {showSaveButton && (
          <SearchHitSaveButtonHolder>
            <SearchHitSaveButtonContainer hit={hit} />
          </SearchHitSaveButtonHolder>
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
